import React, { useState, useEffect, useCallback } from 'react';
import {
  CreditCard,
  Users,
  Gift,
  TrendingUp,
  Calendar,
  Search,
  Plus,
  Edit2,
  Trash2,
  ToggleLeft,
  ToggleRight,
  X,
  Clock,
  DollarSign,
  Percent,
  Tag,
  RefreshCw,
  AlertCircle,
  Crown,
  ArrowUpRight,
} from 'lucide-react';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// ============ Types ============

interface SubscriptionPlan {
  id: string;
  name: string;
  duration_months: number;
  price: number;
  original_price?: number;
  currency: string;
  features: string[];
  popular?: boolean;
  savings_percent?: number;
}

interface PromoCode {
  id: string;
  code: string;
  description?: string;
  discount_percent: number;
  discount_amount: number;
  discount_days: number;
  valid_from: string;
  valid_until?: string;
  max_uses: number;
  current_uses: number;
  applicable_plans: string[];
  is_active: boolean;
  created_at: string;
}

interface Subscription {
  id: string;
  user_id: string;
  plan: string;
  start_date: string;
  end_date: string;
  payment_method: string;
  is_active: boolean;
  created_at: string;
  users?: {
    email: string;
    display_name: string;
    phone_number?: string;
  };
}

interface Payment {
  id: string;
  user_id: string;
  amount: number;
  currency: string;
  status: string;
  payment_method: string;
  transaction_id: string;
  created_at: string;
  users?: {
    email: string;
    display_name: string;
  };
}

interface Stats {
  total_subscriptions: number;
  active_subscriptions: number;
  premium_users: number;
  total_revenue: number;
  monthly_revenue: number;
  currency: string;
  promo_codes_used: number;
  subscriptions_by_plan: Record<string, number>;
}

// ============ API Functions ============

async function fetchStats(): Promise<Stats> {
  const response = await fetch(`${API_BASE_URL}/api/admin/payments/stats`);
  if (!response.ok) throw new Error('Failed to fetch stats');
  return response.json();
}

async function fetchSubscriptions(status?: string): Promise<{ subscriptions: Subscription[] }> {
  const params = status && status !== 'all' ? `?status=${status}` : '';
  const response = await fetch(`${API_BASE_URL}/api/admin/payments/subscriptions${params}`);
  if (!response.ok) throw new Error('Failed to fetch subscriptions');
  return response.json();
}

async function fetchPayments(status?: string): Promise<{ payments: Payment[] }> {
  const params = status ? `?status=${status}` : '';
  const response = await fetch(`${API_BASE_URL}/api/admin/payments/payments${params}`);
  if (!response.ok) throw new Error('Failed to fetch payments');
  return response.json();
}

async function fetchPromoCodes(): Promise<{ promo_codes: PromoCode[] }> {
  const response = await fetch(`${API_BASE_URL}/api/admin/payments/promo-codes`);
  if (!response.ok) throw new Error('Failed to fetch promo codes');
  return response.json();
}

async function fetchPlans(): Promise<{ plans: SubscriptionPlan[] }> {
  const response = await fetch(`${API_BASE_URL}/api/payments/plans`);
  if (!response.ok) throw new Error('Failed to fetch plans');
  return response.json();
}

async function createPromoCode(data: Partial<PromoCode>): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/admin/payments/promo-codes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create promo code');
  }
}

async function updatePromoCode(id: string, data: Partial<PromoCode>): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/admin/payments/promo-codes/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Failed to update promo code');
}

async function deletePromoCode(id: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/admin/payments/promo-codes/${id}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error('Failed to delete promo code');
}

async function togglePromoCode(id: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/admin/payments/promo-codes/${id}/toggle`, {
    method: 'POST',
  });
  if (!response.ok) throw new Error('Failed to toggle promo code');
}

async function cancelSubscription(id: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/admin/payments/subscriptions/${id}/cancel`, {
    method: 'POST',
  });
  if (!response.ok) throw new Error('Failed to cancel subscription');
}

async function extendSubscription(id: string, days: number): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/api/admin/payments/subscriptions/${id}/extend?days=${days}`,
    { method: 'POST' }
  );
  if (!response.ok) throw new Error('Failed to extend subscription');
}

async function grantPremium(userId: string, days: number): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/api/admin/payments/grant-premium?user_id=${userId}&days=${days}`,
    { method: 'POST' }
  );
  if (!response.ok) throw new Error('Failed to grant premium');
}

// ============ Helper Components ============

const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | number;
  subtext?: string;
  trend?: number;
  color: string;
}> = ({ icon, label, value, subtext, trend, color }) => (
  <div className={`bg-white rounded-xl shadow-sm border border-gray-100 p-5`}>
    <div className="flex items-center justify-between">
      <div className={`p-3 rounded-lg ${color}`}>{icon}</div>
      {trend !== undefined && (
        <div
          className={`flex items-center text-sm ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}
        >
          <ArrowUpRight className={`w-4 h-4 ${trend < 0 ? 'rotate-180' : ''}`} />
          <span>{Math.abs(trend)}%</span>
        </div>
      )}
    </div>
    <div className="mt-4">
      <p className="text-2xl font-bold text-gray-800">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
      {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
    </div>
  </div>
);

const Badge: React.FC<{ children: React.ReactNode; variant: 'success' | 'warning' | 'danger' | 'info' }> = ({
  children,
  variant,
}) => {
  const colors = {
    success: 'bg-green-100 text-green-700',
    warning: 'bg-yellow-100 text-yellow-700',
    danger: 'bg-red-100 text-red-700',
    info: 'bg-blue-100 text-blue-700',
  };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[variant]}`}>
      {children}
    </span>
  );
};

const formatCurrency = (amount: number, currency: string = 'XOF') => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'decimal',
    maximumFractionDigits: 0,
  }).format(amount) + ' ' + (currency === 'XOF' ? 'FCFA' : currency);
};

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

// ============ Main Component ============

const PaymentManager: React.FC = () => {
  // State
  const [activeTab, setActiveTab] = useState<'dashboard' | 'subscriptions' | 'payments' | 'promo'>('dashboard');
  const [stats, setStats] = useState<Stats | null>(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [subscriptionFilter, setSubscriptionFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Modal states
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState<PromoCode | null>(null);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [extendingSubscription, setExtendingSubscription] = useState<Subscription | null>(null);
  const [extendDays, setExtendDays] = useState(30);
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [grantUserId, setGrantUserId] = useState('');
  const [grantDays, setGrantDays] = useState(30);

  // Promo form state
  const [promoForm, setPromoForm] = useState({
    code: '',
    description: '',
    discount_percent: 0,
    discount_amount: 0,
    discount_days: 0,
    valid_until: '',
    max_uses: -1,
    applicable_plans: ['monthly', '3months', '6months', 'yearly'],
    is_active: true,
  });

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsData, plansData] = await Promise.all([
        fetchStats(),
        fetchPlans(),
      ]);
      setStats(statsData);
      setPlans(plansData.plans);

      if (activeTab === 'subscriptions') {
        const subsData = await fetchSubscriptions(subscriptionFilter);
        setSubscriptions(subsData.subscriptions);
      } else if (activeTab === 'payments') {
        const payData = await fetchPayments(paymentFilter);
        setPayments(payData.payments);
      } else if (activeTab === 'promo') {
        const promoData = await fetchPromoCodes();
        setPromoCodes(promoData.promo_codes);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [activeTab, subscriptionFilter, paymentFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handlers
  const handleCreatePromo = async () => {
    try {
      if (editingPromo) {
        await updatePromoCode(editingPromo.id, promoForm);
      } else {
        await createPromoCode(promoForm);
      }
      setShowPromoModal(false);
      setEditingPromo(null);
      resetPromoForm();
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  };

  const handleDeletePromo = async (id: string) => {
    if (confirm('Supprimer ce code promo ?')) {
      try {
        await deletePromoCode(id);
        loadData();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur');
      }
    }
  };

  const handleTogglePromo = async (id: string) => {
    try {
      await togglePromoCode(id);
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  };

  const handleCancelSubscription = async (id: string) => {
    if (confirm('Annuler cet abonnement ?')) {
      try {
        await cancelSubscription(id);
        loadData();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur');
      }
    }
  };

  const handleExtendSubscription = async () => {
    if (!extendingSubscription) return;
    try {
      await extendSubscription(extendingSubscription.id, extendDays);
      setShowExtendModal(false);
      setExtendingSubscription(null);
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  };

  const handleGrantPremium = async () => {
    if (!grantUserId) return;
    try {
      await grantPremium(grantUserId, grantDays);
      setShowGrantModal(false);
      setGrantUserId('');
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  };

  const resetPromoForm = () => {
    setPromoForm({
      code: '',
      description: '',
      discount_percent: 0,
      discount_amount: 0,
      discount_days: 0,
      valid_until: '',
      max_uses: -1,
      applicable_plans: ['monthly', '3months', '6months', 'yearly'],
      is_active: true,
    });
  };

  const openEditPromo = (promo: PromoCode) => {
    setEditingPromo(promo);
    setPromoForm({
      code: promo.code,
      description: promo.description || '',
      discount_percent: promo.discount_percent,
      discount_amount: promo.discount_amount,
      discount_days: promo.discount_days,
      valid_until: promo.valid_until ? promo.valid_until.split('T')[0] : '',
      max_uses: promo.max_uses,
      applicable_plans: promo.applicable_plans || [],
      is_active: promo.is_active,
    });
    setShowPromoModal(true);
  };

  // Render tabs
  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<TrendingUp className="w-6 h-6 text-green-600" />}
          label="Revenus totaux"
          value={formatCurrency(stats?.total_revenue || 0)}
          color="bg-green-100"
        />
        <StatCard
          icon={<DollarSign className="w-6 h-6 text-blue-600" />}
          label="Revenus ce mois"
          value={formatCurrency(stats?.monthly_revenue || 0)}
          color="bg-blue-100"
        />
        <StatCard
          icon={<Crown className="w-6 h-6 text-yellow-600" />}
          label="Abonnés actifs"
          value={stats?.active_subscriptions || 0}
          subtext={`${stats?.premium_users || 0} utilisateurs premium`}
          color="bg-yellow-100"
        />
        <StatCard
          icon={<Gift className="w-6 h-6 text-purple-600" />}
          label="Codes promos utilisés"
          value={stats?.promo_codes_used || 0}
          color="bg-purple-100"
        />
      </div>

      {/* Plans Overview */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Plans d'abonnement</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`p-4 rounded-lg border-2 ${
                plan.popular ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'
              }`}
            >
              {plan.popular && (
                <span className="text-xs font-semibold text-indigo-600 uppercase">Populaire</span>
              )}
              <h4 className="font-bold text-gray-800 mt-1">{plan.name}</h4>
              <div className="mt-2">
                <span className="text-2xl font-bold text-gray-900">
                  {formatCurrency(plan.price)}
                </span>
                {plan.original_price && (
                  <span className="text-sm text-gray-400 line-through ml-2">
                    {formatCurrency(plan.original_price)}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 mt-1">{plan.duration_months} mois</p>
              {plan.savings_percent && (
                <Badge variant="success">-{plan.savings_percent}%</Badge>
              )}
              <p className="text-sm text-gray-600 mt-2">
                {stats?.subscriptions_by_plan?.[plan.id] || 0} abonnés
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Actions rapides</h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => {
              resetPromoForm();
              setShowPromoModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            <Gift className="w-4 h-4" />
            Nouveau code promo
          </button>
          <button
            onClick={() => setShowGrantModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
          >
            <Crown className="w-4 h-4" />
            Accorder Premium
          </button>
          <button
            onClick={loadData}
            className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            <RefreshCw className="w-4 h-4" />
            Actualiser
          </button>
        </div>
      </div>
    </div>
  );

  const renderSubscriptions = () => (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <select
          value={subscriptionFilter}
          onChange={(e) => setSubscriptionFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg"
        >
          <option value="all">Tous les abonnements</option>
          <option value="active">Actifs</option>
          <option value="expired">Expirés</option>
        </select>
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher par email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
          />
        </div>
      </div>

      {/* Subscriptions Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Utilisateur</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Plan</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Période</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Statut</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {subscriptions
              .filter((s) =>
                searchQuery
                  ? s.users?.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    s.users?.display_name?.toLowerCase().includes(searchQuery.toLowerCase())
                  : true
              )
              .map((sub) => (
                <tr key={sub.id} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-gray-800">{sub.users?.display_name || 'N/A'}</p>
                      <p className="text-sm text-gray-500">{sub.users?.email || sub.user_id}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-medium text-indigo-600 capitalize">{sub.plan}</span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-gray-600">
                      {formatDate(sub.start_date)} - {formatDate(sub.end_date)}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={sub.is_active ? 'success' : 'danger'}>
                      {sub.is_active ? 'Actif' : 'Expiré'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setExtendingSubscription(sub);
                          setShowExtendModal(true);
                        }}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        title="Prolonger"
                      >
                        <Calendar className="w-4 h-4" />
                      </button>
                      {sub.is_active && (
                        <button
                          onClick={() => handleCancelSubscription(sub.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          title="Annuler"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        {subscriptions.length === 0 && (
          <div className="p-8 text-center text-gray-500">Aucun abonnement trouvé</div>
        )}
      </div>
    </div>
  );

  const renderPayments = () => (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <select
          value={paymentFilter}
          onChange={(e) => setPaymentFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg"
        >
          <option value="">Tous les paiements</option>
          <option value="completed">Complétés</option>
          <option value="pending">En attente</option>
          <option value="failed">Échoués</option>
        </select>
      </div>

      {/* Payments Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Date</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Utilisateur</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Montant</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Méthode</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Transaction</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Statut</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((payment) => (
              <tr key={payment.id} className="border-b last:border-0 hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-600">{formatDate(payment.created_at)}</td>
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-800">{payment.users?.display_name || 'N/A'}</p>
                  <p className="text-sm text-gray-500">{payment.users?.email || payment.user_id}</p>
                </td>
                <td className="px-4 py-3 font-semibold text-gray-800">
                  {formatCurrency(payment.amount, payment.currency)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 capitalize">
                  {payment.payment_method.replace('_', ' ')}
                </td>
                <td className="px-4 py-3">
                  <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                    {payment.transaction_id.slice(0, 12)}...
                  </code>
                </td>
                <td className="px-4 py-3">
                  <Badge
                    variant={
                      payment.status === 'completed'
                        ? 'success'
                        : payment.status === 'pending'
                        ? 'warning'
                        : 'danger'
                    }
                  >
                    {payment.status === 'completed'
                      ? 'Complété'
                      : payment.status === 'pending'
                      ? 'En attente'
                      : 'Échoué'}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {payments.length === 0 && (
          <div className="p-8 text-center text-gray-500">Aucun paiement trouvé</div>
        )}
      </div>
    </div>
  );

  const renderPromoCodes = () => (
    <div className="space-y-4">
      {/* Actions */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-800">Codes promotionnels</h3>
        <button
          onClick={() => {
            resetPromoForm();
            setEditingPromo(null);
            setShowPromoModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        >
          <Plus className="w-4 h-4" />
          Nouveau code
        </button>
      </div>

      {/* Promo Codes Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {promoCodes.map((promo) => (
          <div
            key={promo.id}
            className={`bg-white rounded-xl shadow-sm border p-4 ${
              promo.is_active ? 'border-gray-100' : 'border-red-200 bg-red-50'
            }`}
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Tag className="w-4 h-4 text-indigo-600" />
                  <code className="font-bold text-lg text-indigo-600">{promo.code}</code>
                </div>
                {promo.description && (
                  <p className="text-sm text-gray-600 mt-1">{promo.description}</p>
                )}
              </div>
              <Badge variant={promo.is_active ? 'success' : 'danger'}>
                {promo.is_active ? 'Actif' : 'Inactif'}
              </Badge>
            </div>

            <div className="mt-4 space-y-2">
              {promo.discount_percent > 0 && (
                <div className="flex items-center gap-2 text-sm">
                  <Percent className="w-4 h-4 text-green-600" />
                  <span className="font-semibold text-green-600">-{promo.discount_percent}%</span>
                </div>
              )}
              {promo.discount_amount > 0 && (
                <div className="flex items-center gap-2 text-sm">
                  <DollarSign className="w-4 h-4 text-green-600" />
                  <span className="font-semibold text-green-600">
                    -{formatCurrency(promo.discount_amount)}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Users className="w-4 h-4" />
                <span>
                  {promo.current_uses} / {promo.max_uses === -1 ? '∞' : promo.max_uses} utilisations
                </span>
              </div>
              {promo.valid_until && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span>Expire le {formatDate(promo.valid_until)}</span>
                </div>
              )}
              <div className="flex flex-wrap gap-1 mt-2">
                {promo.applicable_plans?.map((plan) => (
                  <span key={plan} className="text-xs bg-gray-100 px-2 py-1 rounded">
                    {plan}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-4 pt-4 border-t flex items-center justify-end gap-2">
              <button
                onClick={() => handleTogglePromo(promo.id)}
                className={`p-2 rounded hover:bg-gray-100 ${
                  promo.is_active ? 'text-green-600' : 'text-gray-400'
                }`}
                title={promo.is_active ? 'Désactiver' : 'Activer'}
              >
                {promo.is_active ? (
                  <ToggleRight className="w-5 h-5" />
                ) : (
                  <ToggleLeft className="w-5 h-5" />
                )}
              </button>
              <button
                onClick={() => openEditPromo(promo)}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                title="Modifier"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDeletePromo(promo.id)}
                className="p-2 text-red-600 hover:bg-red-50 rounded"
                title="Supprimer"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
      {promoCodes.length === 0 && (
        <div className="bg-white rounded-xl p-8 text-center text-gray-500">
          Aucun code promo. Créez-en un !
        </div>
      )}
    </div>
  );

  // Render
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Gestion des Paiements</h1>
          <p className="text-gray-600">Abonnements, transactions et codes promotionnels</p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex gap-6">
            {[
              { id: 'dashboard', label: 'Tableau de bord', icon: TrendingUp },
              { id: 'subscriptions', label: 'Abonnements', icon: Crown },
              { id: 'payments', label: 'Paiements', icon: CreditCard },
              { id: 'promo', label: 'Codes promo', icon: Gift },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition ${
                  activeTab === tab.id
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-600 hover:text-gray-800'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin" />
          </div>
        ) : (
          <>
            {activeTab === 'dashboard' && renderDashboard()}
            {activeTab === 'subscriptions' && renderSubscriptions()}
            {activeTab === 'payments' && renderPayments()}
            {activeTab === 'promo' && renderPromoCodes()}
          </>
        )}

        {/* Promo Code Modal */}
        {showPromoModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b">
                <h3 className="text-lg font-semibold">
                  {editingPromo ? 'Modifier le code promo' : 'Nouveau code promo'}
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                  <input
                    type="text"
                    value={promoForm.code}
                    onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="PROMO2024"
                    disabled={!!editingPromo}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <input
                    type="text"
                    value={promoForm.description}
                    onChange={(e) => setPromoForm({ ...promoForm, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Description optionnelle"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Réduction (%)
                    </label>
                    <input
                      type="number"
                      value={promoForm.discount_percent}
                      onChange={(e) =>
                        setPromoForm({ ...promoForm, discount_percent: parseInt(e.target.value) || 0 })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      min="0"
                      max="100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Réduction (FCFA)
                    </label>
                    <input
                      type="number"
                      value={promoForm.discount_amount}
                      onChange={(e) =>
                        setPromoForm({ ...promoForm, discount_amount: parseInt(e.target.value) || 0 })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      min="0"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Utilisations max
                    </label>
                    <input
                      type="number"
                      value={promoForm.max_uses}
                      onChange={(e) =>
                        setPromoForm({ ...promoForm, max_uses: parseInt(e.target.value) || -1 })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="-1 pour illimité"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date d'expiration
                    </label>
                    <input
                      type="date"
                      value={promoForm.valid_until}
                      onChange={(e) => setPromoForm({ ...promoForm, valid_until: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plans applicables</label>
                  <div className="flex flex-wrap gap-2">
                    {['monthly', '3months', '6months', 'yearly'].map((plan) => (
                      <label key={plan} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={promoForm.applicable_plans.includes(plan)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setPromoForm({
                                ...promoForm,
                                applicable_plans: [...promoForm.applicable_plans, plan],
                              });
                            } else {
                              setPromoForm({
                                ...promoForm,
                                applicable_plans: promoForm.applicable_plans.filter((p) => p !== plan),
                              });
                            }
                          }}
                          className="rounded"
                        />
                        <span className="text-sm capitalize">{plan}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={promoForm.is_active}
                    onChange={(e) => setPromoForm({ ...promoForm, is_active: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Actif immédiatement</span>
                </label>
              </div>
              <div className="p-6 border-t flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowPromoModal(false);
                    setEditingPromo(null);
                    resetPromoForm();
                  }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Annuler
                </button>
                <button
                  onClick={handleCreatePromo}
                  disabled={!promoForm.code}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {editingPromo ? 'Enregistrer' : 'Créer'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Extend Subscription Modal */}
        {showExtendModal && extendingSubscription && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
              <div className="p-6 border-b">
                <h3 className="text-lg font-semibold">Prolonger l'abonnement</h3>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-gray-600">
                  Prolonger l'abonnement de{' '}
                  <strong>{extendingSubscription.users?.display_name || 'cet utilisateur'}</strong>
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre de jours
                  </label>
                  <input
                    type="number"
                    value={extendDays}
                    onChange={(e) => setExtendDays(parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    min="1"
                    max="365"
                  />
                </div>
              </div>
              <div className="p-6 border-t flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowExtendModal(false);
                    setExtendingSubscription(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Annuler
                </button>
                <button
                  onClick={handleExtendSubscription}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Prolonger
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Grant Premium Modal */}
        {showGrantModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
              <div className="p-6 border-b">
                <h3 className="text-lg font-semibold">Accorder l'accès Premium</h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ID de l'utilisateur
                  </label>
                  <input
                    type="text"
                    value={grantUserId}
                    onChange={(e) => setGrantUserId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="UUID de l'utilisateur"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Durée (jours)</label>
                  <input
                    type="number"
                    value={grantDays}
                    onChange={(e) => setGrantDays(parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    min="1"
                    max="365"
                  />
                </div>
              </div>
              <div className="p-6 border-t flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowGrantModal(false);
                    setGrantUserId('');
                  }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Annuler
                </button>
                <button
                  onClick={handleGrantPremium}
                  disabled={!grantUserId}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50"
                >
                  Accorder
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentManager;
